import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";

export function useTableBodyHeight(offset) {
    const [height, setHeight] = useState(() => Math.max(320, window.innerHeight - offset));

    useEffect(() => {
        const update = () => setHeight(Math.max(320, window.innerHeight - offset));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [offset]);

    return height;
}

const describeLoadError = (err) => {
    const data = err?.response?.data;
    if (typeof data?.detail === "string" && data.detail) return data.detail;
    if (typeof data?.message === "string" && data.message) return data.message;
    if (!err?.response) return "网络异常，请检查网络连接";
    if (err.response.status === 403) return "没有查看的权限";
    return "加载列表失败";
};

// 全局拦截器已经对断网、403、404、5xx 弹过提示。这里只补它不弹的情况，避免两条叠在一起。
const shouldToastLoadError = (err) => {
    if (!err?.isAxiosError) return true;
    const status = err.response?.status;
    if (!status) return false;
    if (status === 403 || status === 404 || status >= 500) return false;
    return true;
};

/**
 * 表格无限滚动：按页追加，筛选或刷新时回到第一页。
 * fetcher 收到 { page, page_size, ...query }，返回带 data.items / data.total 的响应。
 */
export function useInfiniteList({ fetcher, query, pageSize, enabled = true, scrollBodyRef }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [matchesQuery, setMatchesQuery] = useState(false);

    const fetcherRef = useRef(fetcher);
    const queryRef = useRef(query);
    const pageRef = useRef(0);
    const loadingRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const requestIdRef = useRef(0);
    const loadedCountRef = useRef(0);
    const totalRef = useRef(0);
    const matchesQueryRef = useRef(false);
    const itemsQueryKeyRef = useRef("");
    const queryKeyRef = useRef("");

    fetcherRef.current = fetcher;
    queryRef.current = query;
    loadedCountRef.current = items.length;
    totalRef.current = total;
    matchesQueryRef.current = matchesQuery;

    const queryKey = JSON.stringify(query ?? {});
    queryKeyRef.current = queryKey;

    const load = useCallback(
        async ({ page, reset }) => {
            const requestId = reset ? (requestIdRef.current += 1) : requestIdRef.current;
            if (reset) {
                loadingRef.current = true;
                loadingMoreRef.current = false;
                setLoading(true);
                setLoadingMore(false);
            } else {
                if (loadingRef.current || loadingMoreRef.current) return false;
                if (!matchesQueryRef.current) return false;
                if (loadedCountRef.current >= totalRef.current) return true;
                loadingMoreRef.current = true;
                setLoadingMore(true);
            }

            try {
                const response = await fetcherRef.current({
                    page,
                    page_size: pageSize,
                    ...queryRef.current,
                });
                if (requestId !== requestIdRef.current) return false;
                const payload = response?.data;
                if (!payload || !Array.isArray(payload.items) || typeof payload.total !== "number") {
                    throw new Error("列表数据格式不正确");
                }
                const nextItems = payload.items;
                const previousCount = reset ? 0 : loadedCountRef.current;
                if (!reset && nextItems.length === 0) {
                    totalRef.current = previousCount;
                    setTotal(previousCount);
                    pageRef.current = page;
                    return true;
                }
                loadedCountRef.current = previousCount + nextItems.length;
                totalRef.current = payload.total;
                if (reset) itemsQueryKeyRef.current = queryKeyRef.current;
                matchesQueryRef.current = true;
                setMatchesQuery(true);
                setItems((prev) => (reset ? nextItems : [...prev, ...nextItems]));
                setTotal(payload.total);
                pageRef.current = page;
                if (reset) {
                    requestAnimationFrame(() => {
                        const body = scrollBodyRef?.current?.querySelector?.(".ant-table-body");
                        if (body) body.scrollTop = 0;
                    });
                }
                return true;
            } catch (err) {
                if (requestId !== requestIdRef.current) return false;
                if (reset) {
                    const stillCurrent = itemsQueryKeyRef.current === queryKeyRef.current && itemsQueryKeyRef.current !== "";
                    matchesQueryRef.current = stillCurrent;
                    setMatchesQuery(stillCurrent);
                }
                console.error(err);
                if (shouldToastLoadError(err)) message.error(describeLoadError(err));
                return false;
            } finally {
                if (requestId === requestIdRef.current) {
                    loadingRef.current = false;
                    loadingMoreRef.current = false;
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        },
        [pageSize],
    );

    useEffect(() => {
        if (!enabled) {
            requestIdRef.current += 1;
            matchesQueryRef.current = false;
            itemsQueryKeyRef.current = "";
            setMatchesQuery(false);
            setItems([]);
            setTotal(0);
            pageRef.current = 0;
            return;
        }
        load({ page: 1, reset: true });
    }, [enabled, queryKey, load]);

    const reload = useCallback(() => {
        if (!enabled) return;
        load({ page: 1, reset: true });
    }, [enabled, load]);

    const loadMore = useCallback(() => {
        if (!enabled || !matchesQueryRef.current || loadedCountRef.current >= totalRef.current) return;
        load({ page: pageRef.current + 1, reset: false });
    }, [enabled, load]);

    const loadAll = useCallback(async () => {
        if (!enabled || !matchesQueryRef.current) return false;
        let pages = 0;
        while (pages < 200 && loadedCountRef.current < totalRef.current) {
            const before = loadedCountRef.current;
            const ok = await load({ page: pageRef.current + 1, reset: false });
            pages += 1;
            if (!ok || loadedCountRef.current <= before) break;
        }
        return loadedCountRef.current >= totalRef.current;
    }, [enabled, load]);

    const handleTableScroll = useCallback((event) => {
        const target = event?.currentTarget || event?.target;
        if (!target || typeof target.scrollTop !== "number") return;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom < 120) loadMore();
    }, [loadMore]);

    useEffect(() => {
        const root = scrollBodyRef?.current;
        if (!root || !enabled) return undefined;
        const body = root.querySelector(".ant-table-body");
        if (!body) return undefined;
        body.addEventListener("scroll", handleTableScroll, { passive: true });
        return () => body.removeEventListener("scroll", handleTableScroll);
    }, [enabled, items.length, loading, handleTableScroll, scrollBodyRef]);

    return {
        items,
        total,
        loading,
        loadingMore,
        stale: !matchesQuery && items.length > 0,
        hasMore: matchesQuery && items.length < total,
        reload,
        loadMore,
        loadAll,
    };
}
